import { TextArea, TextInput } from 'fluxo-ui';
import { EyeIcon, InfoIcon, TimesIcon } from 'fluxo-ui/icons';

import { MarkdownRenderer } from '../common';

interface QuestionPreviewProps {
    question: {
        type: string;
        title?: string;
        text: string;
        options?: Array<{
            id: string;
            text: string;
            isCorrect: boolean;
            description?: string;
        }>;
        marks?: number;
        timeSeconds?: number;
        defaultMarks?: number;
        defaultTimeSeconds?: number;
        explanation?: string;
        media?: any[];
    };
    showAnswer?: boolean;
    onClose?: () => void;
}

const QuestionPreview: React.FC<QuestionPreviewProps> = ({ question, showAnswer = false, onClose }) => {
    // Build image map from media files
    const imageMap: Record<string, string> = {};
    if (question.media) {
        question.media.forEach((file) => {
            if (file.id && file.url) {
                imageMap[file.id] = file.url;
            }
        });
    }

    const renderQuestionContent = () => {
        const isMCQ = question.type === 'mcq_single' || question.type === 'mcq_multiple';
        const isTrueFalse = question.type === 'true_false';
        const isFillBlanks = question.type === 'fill_blank';
        const isShortAnswer = question.type === 'short_answer';
        const isLongAnswer = question.type === 'long_answer';
        const isCode = question.type === 'code';

        return (
            <div className="space-y-6">
                {/* Question Text */}
                <MarkdownRenderer content={question.text} imageMap={imageMap} />

                {/* Answer Area */}
                {(isMCQ || isTrueFalse) && question.options && (
                    <div className="space-y-3">
                        {question.options.map((option, index) => (
                            <div
                                key={option.id}
                                className={`p-3 rounded-lg border transition-colors ${
                                    showAnswer && option.isCorrect
                                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                                }`}
                            >
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <div className="shrink-0">
                                        {question.type === 'mcq_single' || isTrueFalse ? (
                                            <div className="w-5 h-5 rounded-full border-2 border-slate-300 dark:border-slate-600" />
                                        ) : (
                                            <div className="w-5 h-5 rounded border-2 border-slate-300 dark:border-slate-600" />
                                        )}
                                    </div>
                                    <div className="flex-1 flex items-center gap-2">
                                        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                                            {String.fromCharCode(65 + index)}.
                                        </span>
                                        <span className="text-slate-900 dark:text-white">{option.text}</span>
                                        {showAnswer && option.isCorrect && (
                                            <span className="ml-auto px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
                                                ✓ Correct
                                            </span>
                                        )}
                                    </div>
                                </label>

                                {/* Option Description (shown after submission) */}
                                {showAnswer && option.description && (
                                    <div className="mt-2 ml-8 p-2 bg-slate-50 dark:bg-slate-900 rounded text-sm text-slate-700 dark:text-slate-300">
                                        <div className="flex items-start gap-2">
                                            <InfoIcon className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />
                                            <span>{option.description}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {isFillBlanks && (
                    <div className="space-y-3">
                        <p className="text-sm text-slate-600 dark:text-slate-400">Fill in the blanks to complete the statement:</p>
                        <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <TextInput value="" placeholder="Type your answer..." disabled />
                        </div>
                    </div>
                )}

                {isShortAnswer && (
                    <div className="space-y-3">
                        <p className="text-sm text-slate-600 dark:text-slate-400">Provide a brief answer:</p>
                        <TextInput value="" placeholder="Type your answer..." disabled />
                    </div>
                )}

                {isLongAnswer && (
                    <div className="space-y-3">
                        <p className="text-sm text-slate-600 dark:text-slate-400">Provide a detailed answer:</p>
                        <TextArea value="" placeholder="Type your answer..." rows={6} disabled />
                    </div>
                )}

                {isCode && (
                    <div className="space-y-3">
                        <p className="text-sm text-slate-600 dark:text-slate-400">Write your code solution:</p>
                        <div className="bg-slate-900 dark:bg-slate-950 rounded-lg p-4">
                            <pre className="text-slate-300 font-mono text-sm">
                                <code>// Write your code here...</code>
                            </pre>
                        </div>
                    </div>
                )}

                {/* Explanation (if showing answer) */}
                {showAnswer && question.explanation && (
                    <div className="mt-6 p-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
                        <h4 className="font-semibold text-teal-900 dark:text-teal-100 mb-2 flex items-center gap-2">
                            <EyeIcon className="w-4 h-4" />
                            <span>Explanation</span>
                        </h4>
                        <div className="text-sm text-teal-800 dark:text-teal-200">
                            <MarkdownRenderer content={question.explanation} imageMap={imageMap} />
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-4">
            {/* Preview Header */}
            <div className="flex items-center justify-between p-4 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-3">
                    <EyeIcon className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                    <div>
                        <h3 className="font-semibold text-slate-900 dark:text-white">Question Preview</h3>
                        <p className="text-xs text-slate-600 dark:text-slate-400">This is how students will see the question</p>
                    </div>
                </div>
                {onClose && (
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                    >
                        <TimesIcon className="w-5 h-5" />
                    </button>
                )}
            </div>

            {/* Question Card */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm">
                {/* Question Header */}
                <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            {question.title && (
                                <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-1">{question.title}</h3>
                            )}
                            <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                                <span className="px-2 py-0.5 bg-teal-100 dark:bg-teal-900 text-teal-700 dark:text-teal-300 rounded">
                                    {(question.type || 'unknown').replace(/_/g, ' ').toUpperCase()}
                                </span>
                                {(() => {
                                    const marks = question.marks ?? question.defaultMarks;
                                    const timeSeconds = question.timeSeconds ?? question.defaultTimeSeconds;
                                    return (
                                        <>
                                            {!!marks && (
                                                <span>
                                                    {marks} mark{marks !== 1 ? 's' : ''}
                                                </span>
                                            )}
                                            {!!timeSeconds && (
                                                <span>
                                                    {Math.floor(timeSeconds / 60)}:{(timeSeconds % 60).toString().padStart(2, '0')} mins
                                                </span>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Question Content */}
                <div className="p-6">{renderQuestionContent()}</div>
            </div>
        </div>
    );
};

export default QuestionPreview;
